import { shopOperationError } from "./shopLabels";
import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import { updateAccountShop } from "../auth/apiShops";
import { loadAccountShops, useAccountShops, ACTIVE_SHOP_EVENT } from "./accountShopsStore";
import { elakayAutoText as text } from "../pageInside/dataElakay";
import AddressLocationSelector from "../pageInside/addressLocation/AddressLocationSelector";
import { toLocationValue, fromLocationValue } from "../pageInside/addressLocation/addressLocationData";
// A shop references ONE address of the owner's address book (Shop.addressId):
// pick one, or add a new one (the server adds it to the address book and
// references it). Editing that address in My Addresses updates the shop too.
const fields = [["Address1", "h_msh_address_line1", "Address"], ["Address2", "h_msh_address_line2", "Address line 2"]];
const SHOP_LOCATION_FIELDS = { country: "country", state: "State", city: "city", postalCode: "postalCode" };
const EMPTY_ADDRESS = { Address1: "", Address2: "", city: "", State: "", postalCode: "", country: "" };
const describe = (a) => [a.shopName, a.Address1, a.city, a.State, a.postalCode, a.country].filter(Boolean).join(", ");
export default function ShopAddressEditor({ shop }) {
  const { data } = useAccountShops();
  const addresses = data?.addresses || [];
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("saved");
  const [addressId, setAddressId] = useState("");
  const [address, setAddress] = useState(EMPTY_ADDRESS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (!shop) return null;
  const start = () => {
    const current = addresses.find((a) => String(a._id) === String(shop.addressId))?._id;
    setAddressId(current || addresses.find((a) => a.addressDefault)?._id || addresses[0]?._id || "");
    setMode(addresses.length ? "saved" : "new");
    setAddress(EMPTY_ADDRESS); setError(""); setOpen(true);
  };
  const save = async (event) => {
    event.preventDefault(); setBusy(true); setError("");
    try {
      await updateAccountShop(shop._id, mode === "saved" ? { addressId } : { address });
      await loadAccountShops({ force: true });
      window.dispatchEvent(new CustomEvent(ACTIVE_SHOP_EVENT, { detail: { type: shop.type, shopId: shop._id } }));
      setOpen(false);
    } catch (failure) { setError(shopOperationError(failure)); }
    finally { setBusy(false); }
  };
  return <>
    <button type="button" className="msh-action" onClick={start}>{text("h_multishop_edit_address", "Edit shop address")}</button>
    <Modal show={open} onHide={() => !busy && setOpen(false)} centered>
      <Modal.Header closeButton><Modal.Title>{shop.name} · {text("h_msh_shop_address", "Shop Address")}</Modal.Title></Modal.Header>
      <Modal.Body><form onSubmit={save}>
        {addresses.length > 0 && <div className="msh-radio-row" role="radiogroup" aria-label={text("h_msh_shop_address", "Shop Address")}>
          <label><input type="radio" name={`shop-address-mode-${shop._id}`} checked={mode === "saved"} onChange={() => setMode("saved")} /> {text("h_msh_use_saved_address", "Use one of my addresses")}</label>
          <label><input type="radio" name={`shop-address-mode-${shop._id}`} checked={mode === "new"} onChange={() => setMode("new")} /> {text("h_msh_new_address", "Add a new address")}</label>
        </div>}
        {mode === "saved" && addresses.length > 0 ? <label className="d-block mb-2">{text("h_msh_saved_addresses", "My addresses")}
          <select className="form-control" value={addressId} onChange={(event) => setAddressId(event.target.value)}>
            {addresses.map((a) => <option key={a._id} value={a._id}>{describe(a)}</option>)}
          </select>
        </label> : <>
          {fields.map(([key, label, fallback]) => <label className="d-block mb-2" key={key}>{text(label, fallback)}
            <input className="form-control" required={key === "Address1"} value={address[key] || ""} onChange={(event) => setAddress((old) => ({ ...old, [key]: event.target.value }))} />
          </label>)}
          <AddressLocationSelector
            idPrefix={`shop-address-${shop._id}`}
            value={toLocationValue(address, SHOP_LOCATION_FIELDS)}
            onChange={(location) => setAddress((old) => fromLocationValue(old, location, SHOP_LOCATION_FIELDS))}
            classNames={{ field: "d-block mb-2", input: "form-control" }}
          />
        </>}
        <small className="text-muted d-block mb-2">{text("h_msh_address_by_reference", "The shop uses this address from your address book. Editing it in My Addresses updates every shop that uses it.")}</small>
        {error && <div role="alert">{error}</div>}
        <button className="btn btn-primary" disabled={busy || (mode === "saved" && !addressId)}>{text("h_multishop_save_address", "Save address")}</button>
      </form></Modal.Body>
    </Modal>
  </>;
}
